import React from "react";
import { useApolloClient } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import { HugeiconsIcon } from "@hugeicons/react";
import { Building03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useTranslation } from "react-i18next";
import NewAccountDrawer from "components/platform/accounts/NewAccountDrawer";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { GET_PLATFORM_METRICS } from "graphql/platform";

interface AddAccountActionProps {
  /**
   * A floating button, for a phone, where the navbar has no room for one.
   * Render it last on the page: it sticks to the bottom of the scroll area and
   * comes to rest below the last row, so it never covers that row or the
   * disclosure under it.
   */
  fab?: boolean;
}

/**
 * The platform dashboard's add-account shortcut and the drawer it opens. A new
 * company only moves the dashboard's metrics, so that is all it refetches.
 */
const AddAccountAction: React.FC<AddAccountActionProps> = ({ fab = false }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  // A fresh form per open, so a created or discarded account never comes back.
  const drawer = useDrawerRecord<true>();

  const handleClose = (refresh = false) => {
    drawer.close();
    if (refresh) client.refetchQueries({ include: [GET_PLATFORM_METRICS] });
  };

  const label = t("platform.addAccount");
  const icon = <HugeiconsIcon icon={Building03Icon} size={16} />;

  return (
    <>
      {fab ? (
        <Box
          sx={{
            position: "sticky",
            // Padding rather than a bottom offset: the scroll height leaves out a last child's margin, and this lets the button rest below the last row.
            bottom: 0,
            paddingBottom: "var(--space-6)",
            alignSelf: "flex-end",
            pointerEvents: "none",
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <Fab
            variant="extended"
            onClick={() => drawer.show(true)}
            sx={{ gap: "var(--space-3)", pointerEvents: "auto" }}
          >
            {icon}
            {label}
          </Fab>
        </Box>
      ) : (
        <Button
          variant="default-outlined"
          size="medium"
          startIcon={icon}
          onClick={() => drawer.show(true)}
        >
          {label}
        </Button>
      )}
      {drawer.record && (
        <NewAccountDrawer
          open={drawer.open}
          handleClose={handleClose}
          onAfterClose={drawer.afterClose}
        />
      )}
    </>
  );
};

export default AddAccountAction;
